import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import {
  Home,
  Building2,
  Trees,
  Building,
  Tractor,
  MessageCircle,
  Phone,
  Mail,
  MapPin,
  Clock,
  ChevronRight,
  Award,
  Users,
  Heart,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { SearchFilters } from '../components/SearchFilters';
import { PropertyCard, PropertyModal } from '../components/PropertyCard';
import { useProperties } from '../contexts/PropertyContext';
import { getWhatsAppLink } from '../lib/utils';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

const customIcon = new L.Icon({
  iconUrl: '/favicon.ico', // caminho do favicon
  iconSize: [40, 40], 
  iconAnchor: [20, 40],
  popupAnchor: [0, -35]
});

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const WHATSAPP = '5545999092653';
const PHONE = '+55 45 99909-2653';
const EMAIL = 'negocios@valmircapeletti.com';

// ✅ Coordenadas corretas (lat, lng)
const OFFICE_COORDS = [-24.736300392388824, -53.74194459971138];

// ✅ Tiles mais “modernos” (CartoDB Positron)
const TILE_URL = 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png';
const TILE_ATTR =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 },
};

export default function HomePage() {
  const {
    properties,
    featuredProperties,
    fetchProperties,
    fetchFeaturedProperties,
    updateFilters,
  } = useProperties();

  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchFeaturedProperties();
  }, [fetchFeaturedProperties]);

  const handleSearch = (filters) => {
    fetchProperties(filters);
    setShowSearchResults(true);
    setTimeout(() => {
      document.getElementById('search-results')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleCategoryClick = (propertyType) => {
    updateFilters({ property_type: propertyType });
    fetchProperties({ property_type: propertyType });
    setShowSearchResults(true);
    setTimeout(() => {
      document.getElementById('search-results')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.phone) {
      toast.error('Por favor, preencha nome e telefone.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/leads`, {
        ...contactForm,
        page_source: 'homepage_contact',
      });
      toast.success('Mensagem enviada! Entraremos em contato em breve.');
      setContactForm({ name: '', phone: '', email: '', message: '' });
    } catch (error) {
      toast.error('Erro ao enviar mensagem. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const categories = [
    { type: 'land', label: 'Terrenos', icon: Trees, color: 'bg-emerald-500' },
    { type: 'house', label: 'Casas', icon: Home, color: 'bg-blue-500' },
    { type: 'apartment', label: 'Apartamentos', icon: Building2, color: 'bg-purple-500' },
    { type: 'commercial', label: 'Comerciais', icon: Building, color: 'bg-orange-500' },
    { type: 'rural', label: 'Rurais', icon: Tractor, color: 'bg-green-600' },
  ];

  return (
    <div className="min-h-screen">
      {/* HERO SECTION */}
      <section
        id="hero"
        className="relative min-h-screen flex items-center justify-center pt-20"
        data-testid="hero-section"
      >
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage:
              "url('https://images.pexels.com/photos/323780/pexels-photo-323780.jpeg?auto=compress&cs=tinysrgb&w=1920')",
          }}
        />
        <div className="hero-overlay absolute inset-0" />

        <div className="relative z-10 container mx-auto px-4 md:px-6 text-center">
          <motion.div {...fadeInUp}>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Encontre o imóvel ideal<br />em Toledo e região
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-2xl mx-auto">
              Compra, venda e investimento com atendimento direto e personalizado.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Button
                data-testid="hero-whatsapp-btn"
                asChild
                size="lg"
                className="bg-green-600 hover:bg-green-700 text-white rounded-full h-14 px-8 text-lg"
              >
                <a
                  href={getWhatsAppLink(WHATSAPP, 'Olá! Gostaria de mais informações sobre imóveis.')}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Falar no WhatsApp
                </a>
              </Button>
              <Button
                data-testid="hero-properties-btn"
                size="lg"
                variant="outline"
                className="bg-white/10 border-2 border-white text-white hover:bg-white hover:text-brand-blue rounded-full h-14 px-8 text-lg backdrop-blur-sm"
                onClick={() => document.getElementById('search')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Ver Imóveis
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>

            {/* Trust Bar */}
            <div className="flex flex-wrap justify-center gap-6 text-white/80 text-sm">
              <div className="flex items-center gap-2">
                <Award className="w-7 h-7 text-brand-gold" />
                CRECI 9322-J
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-7 h-7 text-brand-gold" />
                Toledo, PR
              </div>
              <div className="flex items-center gap-2">
                <Building className="w-7 h-7 text-brand-gold" />
                Rua Santos Dumont, 15646
              </div>
            </div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronRight className="w-8 h-8 text-white rotate-90" />
        </div>
      </section>

      {/* SEARCH SECTION */}
      <section id="search" className="py-16 md:py-24 bg-slate-50" data-testid="search-section">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div {...fadeInUp} className="text-center mb-10">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-800 mb-4">
              Encontre seu Imóvel
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Use os filtros para encontrar exatamente o que você procura.
            </p>
          </motion.div>

          <motion.div {...fadeInUp} transition={{ delay: 0.2 }}>
            <SearchFilters onSearch={handleSearch} />
          </motion.div>

          {/* Search Results */}
          {showSearchResults && (
            <div id="search-results" className="mt-12">
              <h3 className="font-heading text-2xl font-semibold text-slate-800 mb-6">
                {properties.length} imóveis encontrados
              </h3>
              {properties.length > 0 ? (
                <div className="property-grid">
                  {properties.map((property) => (
                    <PropertyCard
                      key={property.id}
                      property={property}
                      onClick={setSelectedProperty}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  Nenhum imóvel encontrado com os filtros selecionados.
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* FEATURED PROPERTIES */}
      <section id="featured" className="py-16 md:py-24 bg-white" data-testid="featured-section">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-800 mb-4">
              Imóveis em Destaque
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Seleção especial de oportunidades exclusivas em Toledo e região.
            </p>
          </motion.div>

          <div className="property-grid">
            {featuredProperties.map((property, index) => (
              <motion.div
                key={property.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <PropertyCard property={property} onClick={setSelectedProperty} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section id="categories" className="py-16 md:py-24 bg-slate-50" data-testid="categories-section">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-800 mb-4">
              Busque por Categoria
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Encontre o tipo de imóvel ideal para você.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
            {categories.map((cat, index) => (
              <motion.button
                key={cat.type}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                onClick={() => handleCategoryClick(cat.type)}
                className="category-tile bg-white rounded-2xl p-6 md:p-8 text-center shadow-sm border border-slate-100 hover:border-brand-blue/20"
                data-testid={`category-${cat.type}`}
              >
                <div className={`${cat.color} w-14 h-14 md:w-16 md:h-16 rounded-xl flex items-center justify-center mx-auto mb-4`}>
                  <cat.icon className="w-7 h-7 md:w-8 md:h-8 text-white" />
                </div>
                <h3 className="font-semibold text-slate-800">{cat.label}</h3>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* MISSION / ABOUT */}
      <section id="mission" className="py-16 md:py-24 bg-white" data-testid="mission-section">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeInUp}>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-800 mb-6">
                Compromisso com seus sonhos
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                Com anos de experiência no mercado imobiliário de Toledo e região,
                a Valmir Capeletti – Negócios Imobiliários se dedica a encontrar
                o imóvel perfeito para cada cliente, seja para moradia, investimento
                ou empreendimento comercial.
              </p>
              <p className="text-slate-600 text-lg leading-relaxed mb-8">
                Nosso diferencial está no atendimento personalizado, transparência
                em todas as etapas da negociação e profundo conhecimento do mercado local.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <div className="text-center p-4">
                  <div className="w-12 h-12 bg-brand-blue/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <Award className="w-6 h-6 text-brand-blue" />
                  </div>
                  <h4 className="font-semibold text-slate-800 mb-1">Experiência</h4>
                  <p className="text-sm text-slate-500">Anos de mercado</p>
                </div>
                <div className="text-center p-4">
                  <div className="w-12 h-12 bg-brand-gold/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <Users className="w-6 h-6 text-brand-gold" />
                  </div>
                  <h4 className="font-semibold text-slate-800 mb-1">Atendimento</h4>
                  <p className="text-sm text-slate-500">Personalizado</p>
                </div>
                <div className="text-center p-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <Heart className="w-6 h-6 text-green-600" />
                  </div>
                  <h4 className="font-semibold text-slate-800 mb-1">Confiança</h4>
                  <p className="text-sm text-slate-500">CRECI 9322-J</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <img
                src="/images/Valmir-sobre.png"
                alt="Valmir Capeletti"
                className="rounded-2xl shadow-xl w-full h-auto"
              />
              <div className="absolute -bottom-6 -left-6 bg-brand-blue text-white p-6 rounded-xl shadow-lg">
                <div className="text-3xl font-bold">CRECI</div>
                <div className="text-brand-gold font-semibold">9322-J</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="py-16 md:py-24 bg-slate-50" data-testid="contact-section">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-800 mb-4">
              Entre em Contato
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Estamos prontos para ajudar você a encontrar o imóvel dos seus sonhos.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Map */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="h-[400px] rounded-2xl overflow-hidden shadow-lg"
            >
              <MapContainer center={OFFICE_COORDS} zoom={16} style={{ height: '100%', width: '100%' }}>
                <TileLayer attribution={TILE_ATTR} url={TILE_URL} />
                <Marker position={OFFICE_COORDS} icon={customIcon}>
                  <Popup>
                    <strong>Valmir Capeletti</strong>
                    <br />
                    Rua Santos Dumont, 15646, Sala 02
                    <br />
                    Centro, Toledo – PR
                  </Popup>
                </Marker>
              </MapContainer>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
                <form onSubmit={handleContactSubmit} className="space-y-5" data-testid="contact-form">
                  <div>
                    <Label htmlFor="name">Nome *</Label>
                    <Input
                      id="name"
                      data-testid="contact-name"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      placeholder="Seu nome completo"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Telefone *</Label>
                    <Input
                      id="phone"
                      data-testid="contact-phone"
                      value={contactForm.phone}
                      onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                      placeholder="(45) 99999-9999"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">E-mail</Label>
                    <Input
                      id="email"
                      type="email"
                      data-testid="contact-email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      placeholder="seu@email.com"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="message">Mensagem</Label>
                    <Textarea
                      id="message"
                      data-testid="contact-message"
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      placeholder="Conte-nos sobre o imóvel que você procura..."
                      rows={4}
                      className="mt-1"
                    />
                  </div>
                  <Button
                    type="submit"
                    data-testid="contact-submit"
                    disabled={submitting}
                    className="w-full h-12 bg-brand-blue hover:bg-brand-blue-dark text-white rounded-full"
                  >
                    {submitting ? 'Enviando...' : 'Enviar Mensagem'}
                  </Button>
                </form>

                {/* Contact Info */}
                <div className="mt-8 pt-6 border-t border-slate-100 space-y-4">
                  <a href={`tel:${PHONE}`} className="flex items-center gap-3 text-slate-600 hover:text-brand-blue transition-colors">
                    <div className="w-10 h-10 bg-brand-blue/10 rounded-lg flex items-center justify-center">
                      <Phone className="w-5 h-5 text-brand-blue" />
                    </div>
                    <span>{PHONE}</span>
                  </a>
                  <a
                    href={getWhatsAppLink(WHATSAPP)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-slate-600 hover:text-green-600 transition-colors"
                  >
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <MessageCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <span>WhatsApp</span>
                  </a>
                  <a href={`mailto:${EMAIL}`} className="flex items-center gap-3 text-slate-600 hover:text-brand-blue transition-colors">
                    <div className="w-10 h-10 bg-brand-gold/20 rounded-lg flex items-center justify-center">
                      <Mail className="w-5 h-5 text-brand-gold" />
                    </div>
                    <span>{EMAIL}</span>
                  </a>
                  <div className="flex items-center gap-3 text-slate-600">
                    <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                      <Clock className="w-5 h-5 text-slate-500" />
                    </div>
                    <span>Seg - Sex: 8h às 12h - 14h às 18h </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Property Modal */}
      <PropertyModal
        property={selectedProperty}
        open={!!selectedProperty}
        onClose={() => setSelectedProperty(null)}
      />
    </div>
  );
}